import React, { type HTMLAttributes } from "react";
import camelCase from "lodash/camelCase";
import { cx } from "class-variance-authority";

import { Typography } from "../Typography/Typography";
import { Skeleton } from "../Skeleton/Skeleton";
import * as styles from "./Table.module.scss";

type TextAlign = "left" | "center" | "right";
type TableHeader = {
  label: string;
  color?: string;
  textAlign?: TextAlign;
};
export type TableHeaders = TableHeader[];

type TableProps<T> = {
  name?: string;
  headers: TableHeaders;
  tableData: T[];
  renderRow: (item: any, index: number) => React.ReactNode;
  loading?: boolean;
  skeletonRowCount?: number;
  loadingMore?: boolean;
  emptyMessage?: React.ReactNode;
};

const TableRow = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={cx(styles.tableRow, className)} {...props}>
    {children}
  </tr>
);

const TableCell = ({
  children,
  color,
  textAlign,
  colSpan = 1,
}: {
  children: React.ReactNode;
  color?: string;
  textAlign?: TextAlign;
  colSpan?: number;
}) => (
  <td colSpan={colSpan} style={{ textAlign, color }}>
    {children}
  </td>
);

const TableRowsSkeleton = ({
  skeletonRowCount,
  columnCount,
}: {
  skeletonRowCount: number;
  columnCount: number;
}) => (
  <>
    {Array.from({ length: skeletonRowCount }).map((_, i) => (
      <TableRow key={`skeleton-row-${i}`}>
        <TableCell colSpan={columnCount}>
          <Skeleton size="xs" />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const Table = <T,>({
  name,
  headers,
  tableData: data,
  renderRow,
  loading = false,
  loadingMore = false,
  skeletonRowCount = 1,
  emptyMessage,
}: TableProps<T>) => (
  <table className={styles.table}>
    {name && (
      <caption className={styles.caption}>
        <Typography variant="h4" skeleton={{ loading }}>
          {name}
        </Typography>
      </caption>
    )}
    <thead>
      <TableRow>
        {headers.map(({ label, textAlign, color }) => (
          <th
            key={`${camelCase(label)}-header`}
            className={cx({ [styles.loading]: loading })}
            {...((textAlign || color) && { style: { textAlign, color } })}
          >
            {label}
          </th>
        ))}
      </TableRow>
    </thead>
    <tbody>
      {loading ? (
        <TableRowsSkeleton
          skeletonRowCount={skeletonRowCount}
          columnCount={headers.length}
        />
      ) : data.length === 0 && emptyMessage ? (
        <TableRow>
          <TableCell colSpan={headers.length}>
            <Typography noSpacing>{emptyMessage}</Typography>
          </TableCell>
        </TableRow>
      ) : (
        data.map((item, index) => (
          <React.Fragment key={index}>{renderRow(item, index)}</React.Fragment>
        ))
      )}
      {loadingMore && (
        <TableRowsSkeleton
          skeletonRowCount={skeletonRowCount}
          columnCount={headers.length}
        />
      )}
    </tbody>
  </table>
);

Table.Row = TableRow;
Table.Cell = TableCell;

export { Table };
